'use client';

import {
  useCallback,
  useEffect,
  useRef,
  useState,
  useSyncExternalStore,
} from 'react';

import { MarketingImage } from './marketing-image';
import './produkt-video.css';

export interface ProduktVideoProps {
  /** 1080p-Fassung ab 768 px. */
  src: string;
  /** 720p-Fassung unter 768 px. */
  srcKlein: string;
  posterKlein: string;
  posterGross: string;
  /** Titel der Titelkarte: zugänglicher Name, nicht sichtbar wiederholt. */
  titel: string;
  abspielenLabel: string;
  anhaltenLabel: string;
  /** `sizes` des Titelbilds, passend zum Raster der Seite. */
  sizes: string;
  testId: string;
  /** Im ersten Bild (Hero): Titelbild sofort laden, es ist das LCP-Element. */
  vorrangig?: boolean;
}

/** Unter dieser Breite die kleine Fassung (Telefon). */
export const PRODUKTVIDEO_KLEIN_MEDIA = '(max-width: 767.98px)';

/** Ab diesem Anteil im Bild gilt ein Video als sichtbar. */
export const PRODUKTVIDEO_SICHTBAR_AB = 0.5;

/**
 * Es läuft immer nur ein Video: startet eines, hält das vorige an. Modulweit,
 * weil die Videos einer Seite in verschiedenen Abschnitten und Reitern stehen.
 * `sichtbare` hält die Weckrufe der Videos im Bild, zuletzt sichtbar geworden
 * am Ende: verlässt das laufende Video das Bild, läuft das zuletzt sichtbar
 * gewordene weiter (zwei Videos zugleich im Bild, eines scrollt hinaus).
 */
const lauf: { video: HTMLVideoElement | null } = { video: null };
const sichtbare = new Set<() => boolean>();

function naechstesWecken() {
  for (const wecken of [...sichtbare].reverse()) {
    if (wecken()) return;
  }
}

function alsLaufendMelden(video: HTMLVideoElement) {
  if (lauf.video && lauf.video !== video) lauf.video.pause();
  lauf.video = video;
}

function abmelden(video: HTMLVideoElement | null) {
  if (video && lauf.video === video) lauf.video = null;
}

const keinAbo = () => () => undefined;

function autoplayErlaubt(): boolean {
  if (typeof window === 'undefined') return false;
  if (typeof IntersectionObserver === 'undefined') return false;

  const weniger =
    window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false;
  const sparen =
    (navigator as Navigator & { connection?: { saveData?: boolean } })
      .connection?.saveData === true;

  return !weniger && !sparen;
}

function kleineFassung(): boolean {
  return window.matchMedia?.(PRODUKTVIDEO_KLEIN_MEDIA).matches ?? false;
}

type Zustand = 'ruht' | 'laeuft' | 'pausiert';

/**
 * Stummes Produktvideo (#1760) ohne Videospieler, wie ein GIF.
 *
 * - Ist es zu mindestens der Hälfte im Bild, startet es (stumm, Schleife,
 *   `playsinline`); verlässt es das Bild, hält es an. Versteckte Reiter sind
 *   nie im Bild, also läuft im Reiterwechsel nur das Video des aktiven Reiters.
 * - Ein Tipp auf das Video hält an oder setzt fort. Der kleine Knopf in der
 *   Ecke ist derselbe Schalter für Tastatur und Screenreader. Wer selbst
 *   anhält, dem startet es beim nächsten Sichtbarwerden nicht von allein.
 * - Bei `prefers-reduced-motion: reduce` oder Datensparmodus startet nichts
 *   von allein: Titelbild mit Abspielknopf, auf Tipp läuft es.
 * - Das `<video>` entsteht erst beim ersten Sichtbarwerden (bzw. Tipp); bis
 *   dahin lädt die Seite nur das Titelbild (WebP, lazy, Breite je Gerät).
 *   Das Titelbild liegt über dem Video, bis der erste Frame läuft, also kein
 *   schwarzes Aufblitzen. Unter 768 px kommt die 720p-Fassung.
 *
 * Das Titelbild wird nie beschnitten (16:9): unten stehen die eingebrannte
 * KI-Kennzeichnung (links) und das Logo (rechts), der Schalter sitzt deshalb
 * rechts oben.
 */
export function ProduktVideo({
  src,
  srcKlein,
  posterKlein,
  posterGross,
  titel,
  abspielenLabel,
  anhaltenLabel,
  sizes,
  testId,
  vorrangig = false,
}: ProduktVideoProps) {
  const huelleRef = useRef<HTMLDivElement>(null);
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const [ansicht, setAnsicht] = useState<{
    quelle: string | null;
    zustand: Zustand;
    ersterFrame: boolean;
    /** Der Browser hat stummes Autoplay abgelehnt (iOS-Stromsparmodus). */
    abgelehnt: boolean;
  }>({ quelle: null, zustand: 'ruht', ersterFrame: false, abgelehnt: false });
  // `null` auf dem Server und beim Hydrieren: kein Knopf-Aufblitzen.
  const erlaubt = useSyncExternalStore<boolean | null>(
    keinAbo,
    autoplayErlaubt,
    () => null,
  );
  const automatisch = erlaubt === null ? null : erlaubt && !ansicht.abgelehnt;
  const sichtbarRef = useRef(false);
  const selbstAngehaltenRef = useRef(false);
  const automatischRef = useRef(false);

  useEffect(() => {
    automatischRef.current = automatisch === true;
  }, [automatisch]);

  const quelleWaehlen = useCallback(
    () => (kleineFassung() ? srcKlein : src),
    [src, srcKlein],
  );

  const abspielen = useCallback(() => {
    const video = videoRef.current;

    if (!video) return;

    alsLaufendMelden(video);

    const versuch = video.play();

    // iOS im Stromsparmodus lehnt stummes Autoplay ab: dann wie ohne
    // Autoplay, Titelbild mit Abspielknopf.
    if (versuch && typeof versuch.catch === 'function') {
      versuch.catch(() => {
        automatischRef.current = false;
        setAnsicht((a) => ({ ...a, zustand: 'ruht', abgelehnt: true }));
      });
    }
  }, []);

  const anhalten = useCallback(() => {
    videoRef.current?.pause();
  }, []);

  useEffect(() => {
    const huelle = huelleRef.current;

    if (!huelle || typeof IntersectionObserver === 'undefined') return;

    // Starten, sofern Autoplay gilt und niemand selbst angehalten hat.
    const wecken = () => {
      if (!automatischRef.current || selbstAngehaltenRef.current) return false;

      if (videoRef.current) {
        abspielen();
      } else {
        setAnsicht((a) => ({ ...a, quelle: a.quelle ?? quelleWaehlen() }));
      }

      return true;
    };

    const beobachter = new IntersectionObserver(
      (eintraege) => {
        const eintrag = eintraege[eintraege.length - 1];

        if (!eintrag) return;

        const sichtbar =
          eintrag.isIntersecting &&
          eintrag.intersectionRatio >= PRODUKTVIDEO_SICHTBAR_AB;

        sichtbarRef.current = sichtbar;
        sichtbare.delete(wecken);

        if (!sichtbar) {
          const warLaufend =
            videoRef.current !== null && lauf.video === videoRef.current;

          anhalten();
          if (warLaufend) naechstesWecken();
          return;
        }

        sichtbare.add(wecken);
        wecken();
      },
      { threshold: [0, PRODUKTVIDEO_SICHTBAR_AB] },
    );

    beobachter.observe(huelle);

    return () => {
      beobachter.disconnect();
      sichtbare.delete(wecken);
    };
  }, [abspielen, anhalten, quelleWaehlen]);

  // Das Video ist gerade entstanden: starten, wenn es noch im Bild ist bzw.
  // der Tipp es verlangt hat.
  const videoVerbinden = useCallback(
    (video: HTMLVideoElement | null) => {
      if (!video) {
        abmelden(videoRef.current);
        videoRef.current = null;
        return;
      }

      videoRef.current = video;

      if (selbstAngehaltenRef.current) return;

      if (sichtbarRef.current || !automatischRef.current) abspielen();
    },
    [abspielen],
  );

  const umschalten = () => {
    const video = videoRef.current;

    if (!video) {
      // Erster Tipp ohne Autoplay: Video entsteht und startet.
      selbstAngehaltenRef.current = false;
      setAnsicht((a) => ({ ...a, quelle: quelleWaehlen() }));
      return;
    }

    if (video.paused) {
      selbstAngehaltenRef.current = false;
      abspielen();
    } else {
      selbstAngehaltenRef.current = true;
      anhalten();
    }
  };

  const { quelle, zustand, ersterFrame } = ansicht;
  const laeuft = zustand === 'laeuft';
  // Großer Abspielknopf: angehalten, oder ohne Autoplay vor dem Start.
  const grosserKnopf =
    zustand === 'pausiert' || (zustand === 'ruht' && automatisch === false);

  return (
    <div
      ref={huelleRef}
      className="produktvideo"
      role="group"
      aria-label={titel}
      data-zustand={zustand}
      data-test={testId}
      // Tipp auf die Fläche; Tastatur und Screenreader nutzen den Knopf.
      onClick={umschalten}
    >
      {quelle ? (
        <video
          ref={videoVerbinden}
          className="produktvideo-medium"
          src={quelle}
          muted
          playsInline
          loop
          preload="auto"
          aria-hidden="true"
          tabIndex={-1}
          onPlaying={() =>
            setAnsicht((a) => ({ ...a, zustand: 'laeuft', ersterFrame: true }))
          }
          onPause={() =>
            setAnsicht((a) => ({
              ...a,
              zustand: a.zustand === 'ruht' ? 'ruht' : 'pausiert',
            }))
          }
          data-test={`${testId}-video`}
        />
      ) : null}

      <MarketingImage
        // Ganze Klassenketten statt angehängtem ' is-weg': die Formatierung
        // kürzt führende Leerzeichen in Klassen-Vorlagen.
        className={
          ersterFrame
            ? 'produktvideo-medium produktvideo-poster is-weg'
            : 'produktvideo-medium produktvideo-poster'
        }
        src={posterKlein}
        srcSet={`${posterKlein} 800w, ${posterGross} 1920w`}
        sizes={sizes}
        width={1920}
        height={1080}
        alt=""
        loading={vorrangig ? 'eager' : 'lazy'}
        fetchPriority={vorrangig ? 'high' : undefined}
        decoding="async"
      />

      {grosserKnopf ? (
        <span className="produktvideo-knopf" aria-hidden="true">
          <svg viewBox="0 0 24 24">
            <path d="M8 5.5v13l10.5-6.5z" />
          </svg>
        </span>
      ) : null}

      <button
        type="button"
        className="produktvideo-schalter"
        aria-label={laeuft ? anhaltenLabel : abspielenLabel}
        data-test={`${testId}-schalter`}
      >
        <span className="produktvideo-schalter-feld" aria-hidden="true">
          <svg viewBox="0 0 24 24">
            {laeuft ? (
              <path d="M7 5h3.5v14H7zM13.5 5H17v14h-3.5z" />
            ) : (
              <path d="M8 5.5v13l10.5-6.5z" />
            )}
          </svg>
        </span>
      </button>
    </div>
  );
}
